import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	BuildRecipeParams,
	Crew,
	InformationRetrievalFilters,
	InformationRetrievalPickers,
	InformationRetrievalRow,
	ProducedCombination,
	ProductSize,
	ProductType,
	RecipeMeta,
} from "../models/InformationRetrieval";

/**
 * FLOAT(53) carries IEEE-754 noise in the 15th digit (1.0080000000000005).
 * Six decimals removes it without touching real R-Factor precision, which runs
 * to five (1.02555). The two-decimal rounding used on quantity columns
 * elsewhere would round 1.008 to 1.01 and break parity against legacy.
 */
const roundFloat = (value: number | null): number | null =>
	value === null || value === undefined
		? null
		: Number.parseFloat(value.toFixed(6));

const FLOAT_COLUMNS = [
	"RFactorAvg",
	"RFactorSum",
	"RFactorTargetAvg",
	"RFactorTargetSum",
	"RFactorMinAvg",
	"RFactorMinSum",
	"RFactorMaxAvg",
	"RFactorMaxSum",
	"RFactorRecipeMinAvg",
	"RFactorRecipeMinSum",
	"RFactorRecipeMaxAvg",
	"RFactorRecipeMaxSum",
] as const;

const roundRow = (row: InformationRetrievalRow): InformationRetrievalRow => {
	const rounded = { ...row };
	for (const column of FLOAT_COLUMNS) {
		rounded[column] = roundFloat(row[column]);
	}
	return rounded;
};

export interface GetInformationRetrievalParams
	extends InformationRetrievalFilters {
	signal?: AbortSignal;
}

export const getInformationRetrievalData = async ({
	signal,
	...filters
}: GetInformationRetrievalParams): Promise<InformationRetrievalRow[]> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "start", value: filters.start },
		{ name: "end", value: filters.end },
		{ name: "productTypeId", value: filters.productTypeId },
		{ name: "productSizeId", value: filters.productSizeId },
		// '0' means "all shifts" in the picker, which the procedure expects as
		// NULL; '-' it resolves itself to the crew of the latest billet.
		{
			name: "shiftId",
			value: filters.shiftId === "0" ? null : filters.shiftId,
		},
		{ name: "groupByDate", value: filters.groupByDate },
		{ name: "groupByShift", value: filters.groupByShift },
		{ name: "timeZoneCode", value: null },
		{ name: "isExporting", value: false },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetInformationRetrievalDataWeb]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error fetching information retrieval data",
		);
	}

	const rows = get(
		resp,
		"data.tables[0].rows",
		[],
	) as InformationRetrievalRow[];

	return rows.map(roundRow);
};

/**
 * The dropdowns list only what actually ran in the selected dates — the legacy
 * screen scopes them the same way, which is why production offers two products
 * and not the 200-entry catalog.
 */
export const getInformationRetrievalPickers = async (
	start: string,
	end: string,
	signal?: AbortSignal,
): Promise<InformationRetrievalPickers> => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[MILL].[GetInformationRetrievalPickersDataWeb]",
		[
			{ name: "start", value: start },
			{ name: "end", value: end },
			{ name: "timeZoneCode", value: null },
			{ name: "onlyActives", value: true },
		],
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching filters");
	}

	return {
		crews: get(resp, "data.tables[0].rows", []) as Crew[],
		productSizes: get(resp, "data.tables[1].rows", []) as ProductSize[],
		productTypes: get(resp, "data.tables[2].rows", []) as ProductType[],
		combinations: get(resp, "data.tables[3].rows", []) as ProducedCombination[],
		currentCrewId: get(resp, "data.tables[4].rows[0].CrewId", null) as
			| string
			| null,
	};
};

/**
 * The recipe the modal picks is handed to `CreateRecipeFromBilletsTracked`,
 * which only ever writes to MILL, so the list has to come from the same
 * database as the write.
 */
export const getRecipesMeta = async (
	signal?: AbortSignal,
): Promise<RecipeMeta[]> => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2(
		"[MILL].[GetRecipesMeta]",
		[{ name: "id", value: null }],
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching recipes");
	}
	return get(resp, "data.tables[0].rows", []) as RecipeMeta[];
};

export const buildRecipeFromBilletsTracked = async (
	params: BuildRecipeParams,
): Promise<number | null> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "productSizeId", value: params.productSizeId },
		{ name: "start", value: params.start },
		{ name: "end", value: params.end },
		{ name: "recipeId", value: params.recipeId },
		{ name: "name", value: params.name },
		{ name: "limitsPercentage", value: params.limitsPercentage },
	];

	const resp = await apiService.callV2(
		"[MILL].[CreateRecipeFromBilletsTracked]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error creating recipe from tracked billets",
		);
	}

	return get(resp, "data.tables[0].rows[0].Id", null) as number | null;
};
